import React, { useState } from "react";
import { toast } from "sonner";
import { isNative, isIOS } from "../lib/nativePlatform";
import { isRevenueCatReady, restorePurchases } from "../lib/revenueCat";
import api from "../lib/api";

/**
 * Iter XX.197 — Restore Purchases button.
 *
 * Iter XX.214 — Renders on BOTH native platforms (iOS + Android) when
 * RevenueCat is configured. Copy is platform-aware: iOS mentions
 * "Apple ID", Android mentions "your Google account". Web is a no-op
 * — Stripe subscriptions restore themselves via billing email.
 *
 * Google Play + App Store both require a user-initiated restore
 * surface per store policy.
 *
 * Behaviour:
 *   1. User taps.
 *   2. `Purchases.restorePurchases()` fires — StoreKit / Play resolves
 *      the account's active/recent purchases.
 *   3. If the `plus` entitlement is active on the returned CustomerInfo,
 *      we post `/api/billing/native/refresh` so the backend's entitlement
 *      view flips immediately without waiting for the webhook.
 *   4. UI toasts success / no-purchases-found / error.
 *
 * The button never charges the user and never opens a purchase sheet.
 */
export default function RestorePurchasesButton({
  className   = "",
  label       = "Restore purchases",
  onRestored,
  onNoPurchases,
}) {
  const [busy, setBusy] = useState(false);

  // Only render on native builds where RC has been configured. On web
  // there is nothing to restore (Stripe manages the lifecycle) — the
  // button would just confuse the user.
  if (!isNative()) return null;
  if (!isRevenueCatReady()) return null;

  // Iter XX.214 — Platform-aware copy so iOS users don't see "Google"
  // and Android users don't see "Apple". Preserves the localized
  // store-name convention Apple + Google both prefer in review.
  const iosNative = isIOS();
  const storeName = iosNative ? "Apple ID" : "Google account";
  const busyLabel = iosNative ? "Checking App Store…" : "Checking Google Play…";

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await restorePurchases();
      // Iter XX.241 — Always canonical-sync after a restore attempt.
      // Handles the case where RC's local cache says "no active
      // entitlement" but the RC REST API (or the backend already-
      // written user_billing) knows better. The backend is the
      // authoritative source; a UI "no purchases" toast NEVER fires
      // when the backend has the user marked Plus.
      let canonicalActive = false;
      try {
        const rcAppUserId =
          res?.customerInfo?.originalAppUserId ||
          res?.customerInfo?.original_app_user_id ||
          null;
        const { syncGullyPlus } = await import("../lib/entitlements");
        const canonical = await syncGullyPlus({
          rcAppUserId: rcAppUserId || undefined,
        });
        canonicalActive = !!canonical?.gully_plus?.active;
      } catch { /* swallow */ }

      if ((res.ok && res.plusActive) || canonicalActive) {
        // Legacy refresh kept for one iteration for backwards
        // compat with any listener that observes user_billing
        // directly. Removed in the XX.241 cleanup pass.
        try { await api.post("/billing/native/refresh", {}).catch(() => {}); }
        catch { /* noop */ }
        try {
          toast.success("Your Gully Plus is back.", {
            description: "Every coaching arc is unlocked again.",
          });
        } catch { /* noop */ }
        if (typeof onRestored === "function") onRestored(res.customerInfo);
      } else if (res.ok && !res.plusActive) {
        try {
          toast.message(`No active Gully Plus found on this ${storeName}.`, {
            description: "If you paid recently, wait a minute and try again — or contact support.",
          });
        } catch { /* noop */ }
        if (typeof onNoPurchases === "function") onNoPurchases();
      } else {
        try {
          toast.error("Couldn't check that just now.", {
            description: "No charge was made. Please try again in a moment.",
          });
        } catch { /* noop */ }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="restore-purchases-btn"
      className={
        className ||
        "w-full py-3 rounded-2xl border border-neutral-800 text-neutral-300 text-sm hover:bg-neutral-900 transition-colors disabled:opacity-50"
      }
    >
      {busy ? busyLabel : label}
    </button>
  );
}
